import React, { useEffect, useState, useRef } from "react"
import {
  pdf,
  PDFDownloadLink,
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFViewer,
  Image,
  Font,
  BlobProvider,
} from "@react-pdf/renderer"
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  OutlinedInput,
  Typography,
  Autocomplete,
  TextField,
  Divider,
} from "@mui/material"
import Textarea from "@mui/joy/Textarea"
import Modal from "@mui/material/Modal"
import HighlightOffIcon from "@mui/icons-material/HighlightOff"
import { saveInvoice } from "../../services/invoiceServices"
import { getCommonUsersData } from "../../services/userServices"
import { showToast } from "../GlobalComponents/Toast"
import Loader from "../GlobalComponents/Loader"
import { defaultDataForInvoice } from "./defaultDataForInvoice"
import { uploadFiles, uploadPdfs } from "../../services/fileServices"
import { ERROR_MESSAGE } from "../../utils/constants"
import { debounce } from "lodash"

const styles = StyleSheet.create({
  body: {
    paddingHorizontal: 30,
    fontFamily: "Helvetica",
  },
  letterContent: {
    fontSize: 12,
    lineHeight: 1.4,
    marginBottom: 50,
  },
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },

  headingText: {
    marginTop: 30,
    fontSize: 20,
    fontWeight: "bold",
  },

  breakup: {
    marginTop: 30,
    width: "85%",
  },
  breakupDivider: {
    borderBottom: 1,
    borderBottomColor: "#747a76",
  },
  total: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  totalTitle: {
    width: '70%',
    fontSize: 12,
    marginTop: 2,
    marginBottom: 2,
  },
  totalText: {
    fontSize: 12,
    marginTop: 2,
    marginBottom: 2,
  },
});

const MyDoc = ({ invoiceData }) => {
  return (
    <Document>
      <Page size="A4">
        <Image src="https://res.cloudinary.com/dzpwulxnw/image/upload/v1780647304/header_vrxxdl.png"/>
          <View style={styles.body}>
            <Text style={styles.letterContent}>{invoiceData.letterContent ? invoiceData.letterContent : 'NA'}</Text> 
            
            <Text style={styles.headingText}>Invoice For {invoiceData.invoiceHeadingName ? invoiceData.invoiceHeadingName : "NA"}</Text>

            <View style={styles.breakup}>
              <View style={styles.breakupDivider} />

              {invoiceData?.breakups && invoiceData?.breakups?.map((each, i) => {
                return (
                  <View key={i}>
                    <View style={styles.total}>
                      <Text style={styles.totalTitle}>{each?.title}</Text>
                      <Text style={styles.totalText}>£ {each?.amount}</Text>
                    </View>
                    <View style={styles.breakupDivider} />
                  </View>
                )
              })}

              <View style={styles.total}>
                <Text style={styles.totalTitle}>Total</Text>
                <Text style={styles.totalText}>£ {invoiceData?.totalAmount ? invoiceData?.totalAmount : 0}</Text>
              </View>
            </View>

          </View>
        <Image style={styles.footer} src="https://res.cloudinary.com/dzpwulxnw/image/upload/v1780647303/footer_qc0wao.png" fixed/>
      </Page>
      
    </Document>
  )
}

const PreviewModal = ({ showModal, setShowModal, invoiceData }) => {

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    height: "100%",
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: "10px",
  }

  return (
    <Modal open={showModal} onClose={() => setShowModal(false)}>
      <Box sx={style}>
        <div className="row-between" style={{ padding: "20px" }}>
          <Typography id="modal-modal-title" variant="h8" component="h2">
            {`Preview`}
          </Typography>

          <HighlightOffIcon
            sx={{ cursor: "pointer" }}
            size={30}
            onClick={() => setShowModal(false)}
          />
        </div>
        <PDFViewer style={{ width: "100%", height: "90%", border: "none" }}>
          <MyDoc invoiceData={invoiceData} />
        </PDFViewer>
      </Box>
    </Modal>
  )
}

const InvoiceSave = ({ setShowInvoicesList }) => {

  const data = {
    letterContent: defaultDataForInvoice(), 
    invoiceHeadingName: "Mr Walker Miller (id:829)",
    breakups: [{ 
      title: "Total fee for right eye (DD/MM/YYYY)",
      amount: 6850
    }],
    totalAmount: 6850
  }

  const [patients, setPatients] = useState([])
  const [selectedPatient, setSelectedPatient] = useState("")
  const [showModal, setShowModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [invoiceData, setInvoiceData] = useState(data)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [searchKey, setSearchKey] = useState("")
  const isSearchingRef = useRef(false)

  const refData = useRef({
    patientsInitialData: [],
    isInitialLoad: true,
    setPatientsData: true,
    offset: 0,
    userSearchCount: 0
  })

  const debouncedFilter = React.useCallback(
    debounce((searchKey) => {
      if (refData.current.isInitialLoad) {
        refData.current.isInitialLoad = false
        return
      }
      // setPaginationData({ searchValue: searchKey, count: 1, endReached: false })
      getPatients(searchKey)
    }, 300),
    []
  )

  useEffect(() => {
    debouncedFilter.cancel()
    debouncedFilter(searchKey)
    // console.log("searchKey", searchKey)
  }, [searchKey])

  useEffect(() => {
    getPatients()
  }, [])


  useEffect(() => {
    if (isLoadingMore === false) {
      isSearchingRef.current = false
    }
  }, [isLoadingMore])

  const handleDropdownClose = () => {
    refData.current.setPatientsData = false
    setTimeout(() => {
      refData.current.setPatientsData = true
    }, 1000)
    setPatients(refData.current.patientsInitialData)
    refData.current.userSearchCount = 0
  }

  const getPatients = async (searchKey = "", isLoadMore = false) => {
    if (isLoadingMore || !hasMore) return;
    
    if (refData.current.userSearchCount !== 2) {
      searchKey = ""
    }

    isSearchingRef.current = true;
    setIsLoadingMore(true);

    try {
      const data = await getCommonUsersData({ 
        role: "Patient", 
        searchKey: searchKey, 
        offset: isLoadMore ? refData.current.offset : 0, 
        limit: 20 
      })

      if (data?.result?.length) {
        const pData = data.result.map((each) => {
          let name = each.firstName
          if (each.lastName)
            name += " " + each.lastName + " (id:" + each?.patientId + ")"
          return { ...each, label: name, name, value: each }
        })

        if (searchKey === "") {
          refData.current.patientsInitialData = isLoadMore ? 
            [...refData.current.patientsInitialData, ...pData] : pData;
        }

        if (refData.current.setPatientsData) {
          setPatients(prev => isLoadMore ? [...prev, ...pData] : pData);
          setHasMore(pData.length === 20);
          if (isLoadMore) {
            refData.current.offset++;
          } else {
            refData.current.offset = 1;
          }
        }
      } else {
        setPatients([])
      }
    } catch (error) {
      console.log("getPatients error", error)
    }
    setIsLoadingMore(false);
    isSearchingRef.current = false;
  }

  const handleScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
    
    if (scrollPercentage > 0.8 && !isLoadingMore && hasMore) {
      getPatients(searchKey, true);
    }
  };

  useEffect(() => {
    if (selectedPatient !== "") {
      const fullName = selectedPatient?.title+ " " + selectedPatient?.firstName + " " + selectedPatient?.lastName
      const address1 = selectedPatient?.address1
      const address2 = selectedPatient?.address2
      const town = selectedPatient?.town
      const country = selectedPatient?.country
      const postcode = selectedPatient?. postcode

      const data = defaultDataForInvoice(fullName, address1, address2, town, country, postcode)
      const headingName = `${selectedPatient?.title} ${selectedPatient?.firstName} ${selectedPatient?.lastName} (id:${selectedPatient?.patientId})`
      setInvoiceData({...invoiceData, letterContent: data, invoiceHeadingName: headingName})
    }
  }, [selectedPatient])

  const onClickSaveInvoice = async () => {
    if(invoiceData?.breakups?.find((each) => each?.title === "")) return showToast("error", "Please fill breakup title")
    setIsLoading(true)
    const blob = await pdf(<MyDoc invoiceData={invoiceData} />).toBlob();
    const url = await fileUpload(blob)
    const payload = {
      patient_Id: selectedPatient?._id,
      patientFirstName: selectedPatient?.firstName,
      patientLastName: selectedPatient?.lastName,
      patientId: selectedPatient?.patientId,
      amount: invoiceData?.totalAmount,
      url,
    }
    const response = await saveInvoice(payload)
    if(response?.status === "Success") showToast("info", "Invoice Saved")
    setShowInvoicesList(true)
    setIsLoading(false)
  }

  const fileUpload = async (blob) => {
    try {
      const file = new File([blob], "Invoice.pdf", { lastModified: new Date() })

      const formData = new FormData()
      formData.append("files", file)

      const data = await uploadPdfs(formData)
      console.log("uploadFiles123", data, "data?.data[0]?.newPath", data?.data[0]?.newPath)
      if (!data?.data?.length) {
        let message = data?.message || data?.error || ERROR_MESSAGE
        showToast("error", message)
      } else {
        return data?.data?.[0]?.newPath
      }
    } catch (err) {
      showToast("error", "Something Went Wrong")
    }
  }

  // const fileUpload = async (blob) => {
  //   try {
  //     const file = new File([blob], "Invoice.pdf", { lastModified: new Date() })
  //     const formData = new FormData()
  //     formData.append("file", file)
  //     formData.append("platform", "ayrshire/dev")
  //     const url = "https://upload.aauti.com/file/Upload"
  //     const response = await fetch(url, {
  //       method: "POST",
  //       body: formData,
  //     })
  //     let responseJson = await response.json()
  //     return responseJson?.result
  //   } catch (err) {
  //     showToast("error", "Something Went Wrong")
  //   }
  // }

  const onClickDelete = (data, index) => {
    const updatedData = invoiceData?.breakups?.filter((e, i) => i !== index)
    const amount = invoiceData?.totalAmount - data?.amount
    setInvoiceData({...invoiceData, breakups: updatedData, totalAmount: amount})
  }

  const onChangePatient = (e, v) => {
    setSelectedPatient(v)
    handleDropdownClose()
  }

  return (
    <Box sx={{ paddingX: "10px" }}>
      {isLoading ? 
        <Loader />
      :
        <Box>
          <PreviewModal showModal={showModal} setShowModal={setShowModal} invoiceData={invoiceData} />

          <Box sx={{ display: "flex", justifyContent: 'space-between' }}>
            <Button sx={{mb:2}} variant="contained" onClick={() => setShowInvoicesList(true)}>Back</Button>
            {selectedPatient !== "" && <Box>
              <Button sx={{mr: 2}} variant="contained" onClick={() => setShowModal(true)}>Preview</Button>
              <Button variant="contained" onClick={onClickSaveInvoice}>Save Invoice</Button>
            </Box>}
          </Box>

          <Autocomplete
            options={patients}
            value={selectedPatient}
            onChange={onChangePatient}
            onInputChange={(event, newInputValue) => {
              isSearchingRef.current = true;
              
              if (refData.current.userSearchCount === 0) {
                refData.current.userSearchCount = 1
              } else {
                refData.current.userSearchCount = 2
              }

              setSearchKey(newInputValue)
              refData.current.offset = 0;
              setHasMore(true);
            }}
            onBlur={handleDropdownClose}
            ListboxProps={{
              onScroll: handleScroll,
              style: { maxHeight: '200px' }
            }}
            loading={isSearchingRef.current}
            noOptionsText={isSearchingRef.current ? "Loading..." : "No options"}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Select Patient" 
                size="small"
              />
            )}
          />

          {selectedPatient !== "" && 
            <Box>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <FormLabel htmlFor="letterContent" className="input-label">
                  Letter Content <span className="required">*</span>
                </FormLabel>
                <Textarea
                  id="letterContent"
                  value={invoiceData.letterContent}
                  spellcheck="false"
                  onChange={(e) => setInvoiceData({...invoiceData, letterContent: e.target.value})}
                />
              </FormControl>

              <Box sx={{display: 'flex', alignItems: 'center', mb: 2, mt: 2}}>
                <Typography variant="h5" mr={2}>To add a new breakup</Typography>
                <Button size="small" variant="contained" onClick={() => {
                  setInvoiceData({...invoiceData, breakups: [...invoiceData?.breakups, {title: '', amount: 0}]})
                }}>Click here</Button>
              </Box>
              
              {invoiceData?.breakups?.map((each, i) => {
                return(
                  <Box key={i} sx={{display: 'flex', alignItems: "center", mb:2 }}>
                      <OutlinedInput
                        size="small"
                        sx={{width: '60%'}}
                        value={each?.title}
                        onChange={(event) =>
                          setInvoiceData({...invoiceData, breakups: invoiceData?.breakups?.map((e, index) => {if(i === index){return {...e, title: event.target.value}} else {return e}})})
                        }
                        required
                      />
                      <Box sx={{display: 'flex', alignItems:'center', width: '20%', ml: 4}}>
                        <Typography>£ &nbsp;</Typography>
                        <OutlinedInput
                          type="number"
                          onWheel={(e) => e.target.blur()}
                          size="small"
                          value={each?.amount}
                          onChange={(event) => {
                            const data = invoiceData?.breakups.map((e, index) => {if(i === index){return {...e, amount: event.target.value}} else {return e}})
                            const amount = data.map((e) => e.amount ? parseInt(e.amount) : 0).reduce((a, b) => a + b, 0)
                            console.log('debug',  event.target.value, amount, data)
                            setInvoiceData({...invoiceData, breakups: data, totalAmount: amount})
                            }
                          }
                          required
                        />
                      </Box>
                      {invoiceData?.breakups?.length > 1 && <Button variant="contained" onClick={() => onClickDelete(each, i)}>Delete</Button>}
                  </Box>
                )
              })}

              <Divider sx={{borderBottom: 1}} />

              <Box sx={{display:'flex'}}>
                <Typography width={'61%'} variant="h5" mr={2}>Total</Typography>
                <Typography variant="h6">£ &nbsp; {invoiceData?.totalAmount}</Typography>
              </Box>

              <Box sx={{mb: 2, mt: 2}}>
                <Button sx={{mr: 2}} variant="contained" onClick={() => setShowModal(true)}>Preview</Button>
                <Button variant="contained" onClick={onClickSaveInvoice}>Save Invoice</Button>
              </Box>

            </Box>
          }
        </Box>
      }
    </Box>
  )
}

export default InvoiceSave
